import React, { FC, memo, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Colors, FInter, FlexBox, MagicBox, useIsMobile, useBreakpoint, useToasts, MagicBoxRow } from '@brg/ui';
import { useTranslation } from 'react-i18next';
import styled, { useTheme, css } from 'styled-components';
import { Input } from '@brg/input';
import { API, Endpoints, Profile, Project, ProjectAPI } from '@brg/api';
import { ProfileRole, useProfile } from "@brg/auth";
import { CheckBoxProps } from '@brg/input/dist/checkbox/InputCheckbox';
import { bProfile } from "../../../../core/utils/bProfile";
import ReactTooltip from "react-tooltip";
import { RectangularButtonFilled } from "shared/components/buttons";
import { LayoutConst } from "../../../../core";
import { AppTester } from 'supporting';
import { BilingLabel } from "../../projectEdit/styles";
import { HsePolicies } from 'shared/constants/HsePolicies';
import { ExpertsTagIdsByPolicy, getExpertTags, HasTagExpertPolicy, HasTagModerPolicy, isExpert, isExpertByPolicy, isTagExpert, ModeratedTagsByPolicy } from 'shared/helpers/utils/TagsUtils';
import { useGetFriendlyUrlParam } from 'shared/hooks/useGetFriendlyUrlParam';
import { useRouteMatch } from 'react-router-dom';
import { BlockingReasonsSelect } from './BlockingReasonsSelect/BlockingReasonsSelect';

const YES = 1;
const NO = 0;

// Причины блокировки контента - используем enum значения из бэкенда
export const BLOCKING_REASONS = [
  { value: 0, labelKey: 'project_edit.blocking_reason_adult_content' },
  { value: 1, labelKey: 'project_edit.blocking_reason_hatred_incitement' },
  { value: 2, labelKey: 'project_edit.blocking_reason_criticism_insults' },
  { value: 3, labelKey: 'project_edit.blocking_reason_disinformation' },
  { value: 4, labelKey: 'project_edit.blocking_reason_personal_information' },
  { value: 5, labelKey: 'project_edit.blocking_reason_suicide_drugs' },
  { value: 6, labelKey: 'project_edit.blocking_reason_violence_threats' },
  { value: 7, labelKey: 'project_edit.blocking_reason_extremism' },
  { value: 8, labelKey: 'project_edit.blocking_reason_prohibited_goods' },
  { value: 9, labelKey: 'project_edit.blocking_reason_impersonation' },
  { value: 10, labelKey: 'project_edit.blocking_reason_intellectual_property' },
  { value: 11, labelKey: 'project_edit.blocking_reason_spam' },
  { value: 12, labelKey: 'project_edit.blocking_reason_other' }
];

type ApprovalDialogProps = {
  project: Project;
  onShouldReload?: (project: Project) => void;
  reloadProject?: (time: any) => void;
  isProjectPage?: boolean;
  isBottomMenu?: boolean;
  wide?: boolean;
  fullWidth?: boolean;
  onBlockedStateChange?: (isBlocked: boolean, reasons: number[], otherReason: string) => void;
};

export const CheckBoxWrapper = styled(FlexBox)<{ disabled: boolean }>`
  align-items: center;
  position: relative;
  cursor: ${({ disabled }) => disabled ? 'default' : 'pointer'};
  user-select: none;

`;

export const ApproveTitle = styled(FlexBox)`
  width: 100%;
  justify-content: flex-start;
  font-size: 17px;
  line-height: 22px;
  letter-spacing: -0.34px;
  font-weight: 600;
  color: ${Colors.galBlack73};
  padding-left: 150px;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    font-size: 15px;
    font-style: normal;
    font-weight: 600;
    line-height: 20px; /* 133.333% */
    letter-spacing: -0.3px;
    padding-left: 135px;
  }

  @media (max-width: ${LayoutConst.screen.md}px) {
    padding-left: 0px;
  }

`;

export const ApprovalDialogWrapper = styled(FlexBox)<{ isProjectPage?: boolean, wide?: boolean, fullWidth?: boolean, showBlockingReasons?: boolean }>`
  background-color: ${({ theme }) =>
    AppTester.isZelandTheme() ? Colors.zelWhite10 : theme.mainDropdownStyles.bg};
  border-radius: 8px;
  width: ${({ fullWidth }) => fullWidth ? '100%' : 'calc(100% - 24px)'};
  padding: 25px 24px;
  margin-top: 0;
  justify-content: space-between;
  align-items: flex-start;
  margin-left: ${({ fullWidth }) => fullWidth ? '0' : '24px'};
  position: relative;
  // gap: 16px;

  .left-column-container {
    gap: 16px;
    flex-flow: column;

    @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
      gap: 12px;
    }
  }

  ${({ wide }) => wide && css`
    flex-flow: row;
    align-items: flex-end;

    ${ButtonHolder} {
      width: min-content;
      button {
        margin-left: 0;
      }
    }

    .left-column-container {
      width: 100%;
      max-width: 373px;
    }

    ${ApprovalTitle} {
      width: 204px;
      white-space: nowrap;
    }
  `};
  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    width: ${({ fullWidth }) => fullWidth ? '100%' : 'calc(100% - 24px)'};
    margin-left: ${({ fullWidth }) => fullWidth ? '0' : '24px'};
    margin-top: 0;
    padding: 16px;

    ${({ wide }) => wide && css`
      flex-flow: row;
      align-items: flex-end;
      position: relative;
      flex-direction: column;

      ${ButtonHolder} {
        // position: absolute;
        bottom: 16px;
        right: 16px;
      }

      .left-column-container {
        gap: 16px;
        width: 100%;
      }

      ${ApprovalTitle} {
        width: 140px;
        white-space: pre-line;
      }
  `};

  }
  @media (max-width: ${LayoutConst.screen.md}px) {
    margin-left: ${({ fullWidth }) => fullWidth ? '0' : '12px'};
    padding: 16px;
    width: ${({ fullWidth }) => fullWidth ? '100%' : 'calc(100% - 12px)'};
  }
  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    margin-top: 0;
    margin-left: ${({ fullWidth }) => fullWidth ? '0' : undefined};
    width: ${({ fullWidth }) => fullWidth ? '100%' : undefined};
  }

  ${({ isProjectPage }) => isProjectPage ? css`
    @media (max-width: ${LayoutConst.screen.md}px) {
      min-width: 203px;
      padding: 12px;
      gap: 12px;
    }
    @media (max-width: 780px) {
      width: 100%;
    }
  ` : null};

  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    margin-left: 0px;
    width: 100%;
    .left-column-container {
      gap: 12px;
      width: 100%;
    }
  }

  ${({ showBlockingReasons }) => showBlockingReasons && css`
    @media (max-width: ${LayoutConst.screen.s}px) {
      flex-flow: column;
      align-items: stretch;

      ${ButtonHolder} {
        width: 100%;

        div {
          width: 100%;
        }
      }
    }

    @media (min-width: ${LayoutConst.screen.s + 1}px) {
      flex-flow: row;
      align-items: flex-end;
      gap: 16px;

      .left-column-container {
        flex: 1;
        min-width: 0;
      }

      ${ButtonHolder} {
        width: auto;
        flex-shrink: 0;
      }
    }
  `}
`;

export const ContentWrapper = styled(FlexBox)<{ isProjectPage?: boolean }>`
  width: 100%;

  ${BilingLabel} {
    font-size: 15px !important;
    line-height: 20px !important;

    @media (max-width: ${LayoutConst.screen.m}px) {
      font-size: 13px !important;
      line-height: 16px !important;
    }
  }

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
  }

  ${({ isProjectPage }) => isProjectPage ? css`

    @media (max-width: ${LayoutConst.screen.md}px) {
      flex-direction: column;
      justify-content: center;
    }

    @media (max-width: ${LayoutConst.screen.s - 1}px) {
      flex-direction: row;
    }
  ` : null};
`;

export const RadioButtonWrapper = styled(FlexBox)<{ isProjectPage?: boolean }>`

  ${({ isProjectPage }) => isProjectPage ? css`
    @media (max-width: ${LayoutConst.screen.md}px) {
      margin-top: 0 !important
    }

    @media (max-width: ${LayoutConst.screen.s - 1}px) {
      margin-bottom: 0;
    }
  ` : null};
`;

export const RadioBox = styled.div<{ isBook?: boolean, approval?: boolean, firstRadioButton?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-direction: ${p => p.isBook ? "column" : "row"};

  :first-of-type {
    margin-right: 16px;
    @media (max-width: ${LayoutConst.screen.md}px) {
    ${({approval, firstRadioButton}) => approval && !firstRadioButton && css`
    margin-right: 0px;
  `}
  
  }
  }
`

export const RadioCircle = styled(FlexBox)<CheckBoxProps & { clr?: string }>`
  width: 24px;
  height: 24px;
  background: ${p => p.theme.approvalBlockStyles.radioCircle};
  border: 1px solid ${p => p.clr ? p.clr : '#B6B6B5'};
  box-sizing: border-box;
  border-radius: 12px;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  svg {
    opacity: 0;
    transition: all 0.2s;
  }

  opacity: ${p => p.disabled ? 0.31 : 1};
`;

export const RadioLabel = styled(FInter.SmallTextBold)<{ clr?: string }>`
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 129.412% */
  letter-spacing: -0.34px;
  color: ${p => p.clr ? p.clr : '#545453'};

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    font-size: 13px;
    font-style: normal;
    line-height: 16px; /* 133.333% */
    letter-spacing: -0.3px;
  }
    
`

export const ApproveProjectButton = styled(RectangularButtonFilled)<{ isBook?: boolean, isProjectPage?: boolean }>`
  font-size: 17px;
  line-height: 22px;
  letter-spacing: -0.02em;
  font-weight: 600;
  padding: 4px 32px;
  height: 40px !important;
  min-width: 85px;
  margin-left: ${p => p.isBook ? "0px" : "9.5px"};
  max-width: 127px;
  color: ${({theme}) => theme.approvalBlockStyles.buttonColor};
  background: ${({theme}) => theme.approvalBlockStyles.buttonBg};
  margin-top: 8px;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    font-size: 15px;
    line-height: 22px;
    letter-spacing: normal;
    font-weight: 700;
    //height: 24px;
    margin-left: 0;
    min-width: 94px;
    width: 100% !important;
    max-width: initial !important;
  }
  @media (max-width: ${LayoutConst.screen.md}px) {
    margin-top: 0;
    min-width: 103px;
    //width: 103px;
  }
  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    min-width: 143px;
    //width: 143px;
  }


    /*    @media(min-width: 1920px) {
      margin-left: ${p => p.isBook ? "0px" : "20px"};
      font-size: 13px;
      line-height: 18px;
      padding: 6px 16px;
      height: 30px;
      min-width: 103px;
    }*/

  ${({ isProjectPage, isBook }) => isProjectPage ? css`
    @media (max-width: ${LayoutConst.screen.md}px) {
      margin-left: 0;
    }

    @media (max-width: ${LayoutConst.screen.s - 1}px) {
      margin-left: 0;
    }
  ` : null};
`;

export const RadioInputSource = styled.input`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 100;
  height: 100%;
  cursor: ${({ disabled }) => disabled ? 'default' : 'pointer'};
  opacity: 0;

  &:checked {
    & ~ ${RadioBox} {

      ${RadioCircle} {
        border: 1px solid ${Colors.galBlack47};

        :after {
          content: "";
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: ${p => p.theme.approvalBlockStyles.radioDot};
        }
      }

      // ${RadioLabel} {
      //   color: #545453;
      // }
    }
`;

export const ApprovalTitle = styled(FInter.SmallTextBold)<{ isBook?: boolean, isProjectPage?: boolean }>`
  font-size: 15px;
  line-height: 20px; /* 129.412% */
  letter-spacing: -0.34px;
  font-weight: 400;
  color: ${Colors.galBlack47};
  margin-right: ${p => p.isBook ? "0px" : "24px"};
  width: 231px;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    font-size: 15px;
    line-height: 20px;
    font-style: normal;
    width: 140px;

    line-height: 20px; /* 133.333% */
    letter-spacing: -0.3px;
    margin-right: ${p => p.isBook ? "0px" : "24px"};
  }

  @media (max-width: ${LayoutConst.screen.md}px) {
    width: 100%;
    font-size: 13px;
    line-height: 16px;
    letter-spacing: -0.26px;
  }

  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    width: auto;
  }


  ${({ isProjectPage, isBook }) => isProjectPage ? css`
    @media (max-width: ${LayoutConst.screen.md}px) {
      margin-bottom: 0px;
      margin-right: 8px;
      width: 100%;
    }

    @media (max-width: ${LayoutConst.screen.s - 1}px) {
      margin-bottom: 0;
      margin-right: ${isBook ? "0px" : "40px"};
    }
  ` : null};
`;

export const getApprovalFontColor = (clr: string) => {
  const list = ["#FFFFFF", "#D52B1E", "#FFC19C", "#FFBC3D", "#BED600", "#3D7EDB", "#AACAE6", "#F5EC5A", "#000000", "#D1D4D3", "#5E6A71", "#E2DDCB", "#AEA79F", "#BCD473", "#394A58"];
  const list_1 = [list[1], list[2], list[3], list[4], list[6], list[8], list[9], list[11], list[13]];

  return !list_1.includes(clr) ? Colors.galBlack47 : Colors.galBlack21;
  // #909090 - темно серый
  // ##CDCDCD - серый
}

const CheckboxHolder = styled(FlexBox)<{ isBottomMenu?: boolean }>`

  ${({ isBottomMenu }) => isBottomMenu && css`
    div > {
      // :first-child {
      //   background-color: #fefefe !important;
      // }
    }
  `};

  ${BilingLabel} {
    font-size: 17px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px; /* 129.412% */
    letter-spacing: -0.34px;
    color: ${Colors.galBlack47};

    @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
      font-size: 15px;
      font-style: normal;
      line-height: 20px; /* 133.333% */
      letter-spacing: -0.3px;
    }
    @media (max-width: ${LayoutConst.screen.md}px) {
     font-size: 13px;
      line-height: 16px; /* 123.077% */
      letter-spacing: -0.26px;
    }
  }
`

const ButtonHolder = styled(FlexBox)`
  width: 100%;
  justify-content: end;
  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    width: 100%;

    div {
      width: 127px;
    }
  }

  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    div {
      width: 100%;
    }
  }
`

const BlockingReasonsWrapper = styled(FlexBox)<{ showBlockingReasons?: boolean }>`
  flex-direction: column;
  width: 100%;
  margin: 16px 0;
  position: relative;
  z-index: 100;

  ${({ showBlockingReasons }) => showBlockingReasons && css`
    margin: 16px 0 0 0;
  `}

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    margin: 16px 0 0 0;
  }
`;

const BlockingReasonsLabel = styled(FInter.SmallTextBold)`
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.34px;
  color: ${Colors.galBlack47};
  margin-bottom: 8px;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    font-size: 13px;
    line-height: 16px;
    letter-spacing: -0.3px;
  }
`;

const OtherReasonInputWrapper = styled(FlexBox)`
  flex-direction: column;
  margin-top: 8px;
`;

const OtherReasonInput = styled(Input.Text)`
  max-width: 100%;
  border: none !important;
  color: ${p => p.theme.themeName === 'blackTheme' || p.theme.themeName === 'greyTheme' || p.theme.themeName === 'jhonataTheme' ? '#FFFFFF' : p.theme.dropdown.textColor} !important;
  background-color: ${p => p.theme.approvalBlockStyles?.selectBg ?? p.theme.dropdown.bgColor} !important;
  
  &:focus {
    border: none !important;
    outline: none !important;
    color: ${p => p.theme.themeName === 'blackTheme' || p.theme.themeName === 'greyTheme' || p.theme.themeName === 'jhonataTheme' ? '#FFFFFF' : p.theme.dropdown.textColor} !important;
  }
  
  &:focus-visible {
    outline: none !important;
  }
  
  input {
    padding-left: 8px !important;
    color: ${p => p.theme.themeName === 'blackTheme' || p.theme.themeName === 'greyTheme' || p.theme.themeName === 'jhonataTheme' ? '#FFFFFF' : p.theme.dropdown.textColor} !important;
    background-color: ${p => p.theme.approvalBlockStyles?.selectBg ?? p.theme.dropdown.bgColor} !important;
    
    &::placeholder {
      transition: none !important;
      color: ${p => p.theme.themeName === 'blackTheme' || p.theme.themeName === 'greyTheme' || p.theme.themeName === 'jhonataTheme' ? '#FFFFFF' : p.theme.dropdown.textColor} !important;
      opacity: 0.7 !important;
    }
    
    &:hover::placeholder {
      opacity: 0.7 !important;
      color: ${p => p.theme.themeName === 'blackTheme' || p.theme.themeName === 'greyTheme' || p.theme.themeName === 'jhonataTheme' ? '#FFFFFF' : p.theme.dropdown.textColor} !important;
    }
  }

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    height: 40px !important;
  }
`;


const ApprovalDialog: FC<ApprovalDialogProps> = ({
  project: initialProject,
  reloadProject,
  isProjectPage,
  isBottomMenu,
  wide,
  fullWidth,
  onBlockedStateChange
}) => {
  const { t } = useTranslation();
  const profile = useProfile();
  const theme = useTheme();
  const id = useGetFriendlyUrlParam("id");
  const isPlayerOrMatchRoute = Boolean(useRouteMatch(['/player/:id', '/match/:id']));
  const toasts = useToasts();
  const [project, setProject] = useState(initialProject)
  const [projectAgeRestricted, setProjectAgeRestricted] = useState<boolean>(false);
  const [projectBanned, setProjectBanned] = useState<boolean>(false);
  const [projectNominated, setProjectNominated] = useState<boolean>(false)
  const [lastNomination, setLastNomination] = useState<string | undefined>()
  const [blockingReasons, setBlockingReasons] = useState<number[]>([]);
  const [otherReasonText, setOtherReasonText] = useState<string>('');
  const reasonsLoadedRef = useRef<string | null>(null);
  
  useEffect(() => {
    const projectAny = project as any;
    const projectId = project?.projectId;
    
    if (projectId && reasonsLoadedRef.current !== projectId) {
      if (project?.status === 3 && projectAny?.lastBannedInfo?.bannedReasonInfo) {
        const { reasons, otherReason } = projectAny.lastBannedInfo.bannedReasonInfo;
        
        if (reasons && Array.isArray(reasons)) {
          setBlockingReasons(reasons);
          
          const otherReasonValue = otherReason || '';
          setOtherReasonText(otherReasonValue);
          
          reasonsLoadedRef.current = projectId;
        }
      } else {
        if (reasonsLoadedRef.current !== projectId) {
          setBlockingReasons([]);
          setOtherReasonText('');
          reasonsLoadedRef.current = projectId;
        }
      }
    }
  }, [project]);
  
  const isTagExpert = isExpert(profile, project?.tagsID)
  const projectTagAndGroupIds = [
    ...(project?.tagsID || []),
    ...(project?.tags?.flatMap((tag: any) => tag?.groupIds || []) || []),
  ];
  const isTagExpertByPolicy = isExpertByPolicy(profile, projectTagAndGroupIds);


  const hasApproveProjectLimb = (profile: Profile<"Local"> | undefined) => profile && profile.rolesList && Array.isArray(profile.rolesList)
    && (new ProfileRole(HsePolicies.can_approve_project_limb)).containsIn(profile.rolesList);

  const isJuryMember = (profile: Profile<"Local"> | undefined) => profile && profile.rolesList && Array.isArray(profile.rolesList)
    && (new ProfileRole(HsePolicies.is_jury_member)).containsIn(profile.rolesList);

  const isTagModer = useMemo(() => {
    if (profile) {
      const { tags: projectTags = [] } = project;
      // @ts-ignore
      const { tagListId: moderatedTags } = profile;
      // @ts-ignore
      const isTagModerResult = moderatedTags.length && projectTags && projectTags.some(({
        tagId,
        // @ts-ignore
        isEducationTag
      }) => isEducationTag && (moderatedTags.includes(tagId)));

      if (isTagModerResult) {
        return isTagModerResult;
      }

      if (hasApproveProjectLimb(profile)) {
        return true;
      }

      if (!HasTagModerPolicy(profile)) {
        return false;
      }

      const moderatedTagsByPolicy = ModeratedTagsByPolicy(profile);

      const isTagModerByPolicy = projectTags && projectTags.some(({
        tagId,
        // @ts-ignore
        isEducationTag
      }) => isEducationTag && moderatedTagsByPolicy?.includes(tagId));

      return isTagModerByPolicy;
    }
    return false;
  }, [project, profile]);

  const isTagModer_Expert = useMemo(() => {
    if (profile) {
      const { tags: projectTags = [] } = project;
      // @ts-ignore
      const { tagsExpert: moderatedTags_Expert } = profile;

      // @ts-ignore
      const isTagModerExpert = isTagExpert && moderatedTags_Expert?.length && projectTags && projectTags.some(({
        tagId,
        isEducationTag
      }) => /*isEducationTag &&*/ getExpertTags(moderatedTags_Expert).includes(tagId));

      if (isTagModerExpert) {
        return isTagModerExpert;
      }

      if (!HasTagExpertPolicy(profile)) {
        return false;
      }

      const moderatedTagsExpertByPolicy = ExpertsTagIdsByPolicy(profile);

      const isTagModerExpertByPolicy = isTagExpertByPolicy && projectTags && projectTags.some(({
        tagId,
        groupIds = [],
      }: any) => moderatedTagsExpertByPolicy?.includes(tagId)
        || groupIds.some((groupId: string) => moderatedTagsExpertByPolicy?.includes(groupId)));

      return isTagModerExpertByPolicy;
    }
    return false;
  }, [project, profile, isTagExpertByPolicy]);

  const canChangeShowCase = useMemo(() => {
    if (bProfile.isModer(profile)) return true;
    if (bProfile.isMiddleModer(profile)) return true;
    if (isTagModer_Expert && (bProfile.isExpertModer(profile) || isJuryMember(profile))) return true;
    if (profile && profile?.rolesList && Array.isArray(profile?.rolesList)
      && (new ProfileRole(HsePolicies.can_approve_project_main)).containsIn(profile?.rolesList || [])) return true;

    return false;
  }, [isTagModer_Expert, isJuryMember, profile])

  const canChangeLimb = useMemo(() => {
    if (canChangeShowCase) return true;
    if (bProfile.isJuniorModer(profile)) return true;
    if (isTagModer) return true;
    if (profile && profile?.rolesList && Array.isArray(profile?.rolesList)
      && (new ProfileRole(HsePolicies.can_approve_project_limb)).containsIn(profile?.rolesList || [])) return true;

    return false;
  }, [canChangeShowCase, isTagModer, profile])

  const canChangeBannedStatus = useMemo(() => {
    if (bProfile.isModer(profile)) return true;
    if (profile && profile?.rolesList && Array.isArray(profile?.rolesList)
      && (new ProfileRole(HsePolicies.can_change_banned_status_project)).containsIn(profile?.rolesList || [])) return true;

    return false;
  }, [project, profile])

  const onHseDesign = useMemo(() => !!project.contexts?.includes('hsedesign'), [project]);
  const onHseDesignTemp = useMemo(() => !!project.contexts?.includes('hsedesigntemp'), [project]);

  const isChapter = useMemo(() => (id === project?.projectId && !!project?.book), [project, id])

  const [selected, setSelected] = useState<number | undefined>();
  const [selectedLimb, setSelectedLimb] = useState<number | undefined>();

  useEffect(() => {
    const handleApprovalChange = (event: CustomEvent) => {
      const { target, value } = event.detail;
      if (target === 'limb') {
        setSelectedLimb(value);
      } else if (target === 'main') {
        setSelected(value);
      }
    };

    window.addEventListener('approval-radio-change', handleApprovalChange as any);
    
    return () => {
      window.removeEventListener('approval-radio-change', handleApprovalChange as any);
    };
  }, []);

  // @ts-ignore
  const { approvals, isAgeRestricted, contexts, status } = project;

  const lastApprovals = [...(approvals || [])].reverse()

  // @ts-ignore
  const sortedLastApprovals = lastApprovals.sort((a, b) => new Date(b?.date) - new Date(a?.date))
  const lastHSEApproveLimbIndex = sortedLastApprovals.findIndex((approve: any) => ['hsedesigntemp'].includes(approve.contextService));
  const lastHSEApproveIndex = sortedLastApprovals.findIndex((approve: any) => ['hsedesign'].includes(approve.contextService));

  const moderationInfo = useMemo(() => {
    if (lastHSEApproveIndex !== -1 && lastHSEApproveLimbIndex !== -1) {
      return sortedLastApprovals[lastHSEApproveIndex];
    }
    return (lastHSEApproveIndex === -1 || onHseDesignTemp) ? undefined : sortedLastApprovals[lastHSEApproveIndex]
  }, [lastHSEApproveIndex, lastHSEApproveLimbIndex, onHseDesignTemp, sortedLastApprovals])

  const moderationInfoLimb = useMemo(() => {

    // return (lastHSEApproveLimbIndex === -1 || (lastHSEApproveIndex < lastHSEApproveLimbIndex)) ? undefined : sortedLastApprovals[lastHSEApproveLimbIndex]
    return /*(lastHSEApproveLimbIndex === -1 || onHseDesign) ? undefined : */sortedLastApprovals[lastHSEApproveLimbIndex]
  }, [lastHSEApproveLimbIndex, onHseDesign, sortedLastApprovals])

  const { lastApprovalIsOwn, lastApprovalLimbIsOwn, lastApprovesExpert } = useMemo(() => {

    if (!canChangeLimb) {
      return {
        lastApprovalIsOwn: false,
        lastApprovalLimbIsOwn: false,
      };
    }

    return {
      lastApprovalIsOwn: Boolean(!!moderationInfo && moderationInfo?.accountId === profile?.accountId),
      lastApprovalLimbIsOwn: Boolean(!!moderationInfoLimb && moderationInfoLimb?.accountId === profile?.accountId),
      lastApprovesExpert: (
        moderationInfo?.role?.includes('expertmoder') || moderationInfo?.role?.includes('moder') || 
        moderationInfo?.role?.includes('supermoder') || moderationInfo?.role?.includes('middlemoder'))  && 
        !canChangeShowCase && moderationInfo && moderationInfo.accountId !== profile?.accountId
    }
  }, [canChangeLimb, moderationInfo, profile?.accountId, moderationInfoLimb, canChangeShowCase]);

  const showModal = useMemo(() => {
    if (isChapter) return false;
    return canChangeShowCase || canChangeLimb || canChangeBannedStatus

  }, [isChapter, canChangeShowCase, canChangeLimb])

  const lastLimbStatus = useMemo(
    () => (sortedLastApprovals?.find(
      ({ contextService }) => contextService === 'hsedesigntemp')?.isApproved)
    , [sortedLastApprovals])

  useEffect(() => {
    if (selectedLimb === undefined && lastLimbStatus !== undefined) {
      setSelectedLimb(lastLimbStatus ? YES : NO);
    }
  }, [selectedLimb, lastLimbStatus]);

  const isApproved = useMemo(() => {
    return moderationInfo?.isApproved
  }, [moderationInfo])
  const isApprovedLimb = useMemo(() => {
    return moderationInfoLimb?.isApproved
  }, [moderationInfoLimb])

  const canApprove = useMemo(() => {
    if (isApproved) return false
    if (!canChangeShowCase) return false

    return true
  }, [isApproved, canChangeShowCase])
  const canApproveLimb = useMemo(() => {
    if (isApprovedLimb) return false
    if (!canChangeLimb) return false
    if (!lastApprovalLimbIsOwn && moderationInfoLimb) return false

    return true
  }, [isApprovedLimb, canChangeLimb, lastApprovalLimbIsOwn])

  const translateLastApproval = useMemo(
    () => {
      const currentApproveState = (
        canChangeShowCase ? moderationInfo?.isApproved : moderationInfoLimb?.isApproved
      );

      if (currentApproveState === undefined) {
        return undefined
      }

      return !!currentApproveState ? YES : NO
    }
    , [moderationInfo, canChangeShowCase, moderationInfoLimb])

  const disableSaveContexts = useMemo(() => {

      if (lastApprovesExpert && !canChangeShowCase && canChangeLimb) return true;

      if (!canChangeShowCase && !lastApprovalLimbIsOwn && moderationInfoLimb) return true

      if (canChangeShowCase) {
        // if (selected === 1 && !canApprove) return true
        if (translateLastApproval === selected) return true
        // if (selected === 0 && !canDisapprove && approvals.length > 0) return true

        return selected === undefined
      }

      if (translateLastApproval === selectedLimb && !canApproveLimb) return true
      // if (selectedLimb === 0 && !canDisapproveLimb && approvals.length > 0) return true

      return selectedLimb === undefined

    }
    , [canChangeShowCase, lastApprovalLimbIsOwn, isApproved, isTagModer, lastApprovesExpert, moderationInfo, moderationInfoLimb, selectedLimb, canApproveLimb, selected, canApprove]
  )

  const canSaveAgeRestriction = useMemo(() => {
    //@ts-ignore
    return !!project?.isAgeRestricted !== projectAgeRestricted;
  }, [project, projectAgeRestricted])

  const canSaveBannedStatus = useMemo(() => {
    const initialProjectIsBanned = status === 3
    //@ts-ignore
    const statusChanged = initialProjectIsBanned !== projectBanned;
    
    if (projectBanned && blockingReasons.length === 0) {
      return false;
    }
    
    if (projectBanned && blockingReasons.includes(12) && !otherReasonText.trim()) {
      return false;
    }
    
    return statusChanged;
  }, [status, projectBanned, blockingReasons, otherReasonText])

  const canSaveBlockingReasons = useMemo(() => {
    if (!projectBanned) return false;
    
    if (blockingReasons.includes(12) && !otherReasonText.trim()) {
      return false;
    }
    
    if (blockingReasons.length === 0) return false;
    
    // Временно: запрещаем изменение причин если они уже сохранены (до реализации на бэке)
    const projectAny = project as any;
    const savedReasons = projectAny?.lastBannedInfo?.bannedReasonInfo?.reasons || [];
    
    if (savedReasons.length > 0) {
      return false;
    }
    
    const savedOtherReason = projectAny?.lastBannedInfo?.bannedReasonInfo?.otherReason || '';
    
    const reasonsChanged = JSON.stringify([...blockingReasons].sort()) !== JSON.stringify([...savedReasons].sort());
    const currentOtherReason = otherReasonText.trim();
    const savedOtherReasonNormalized = savedOtherReason?.trim() || '';
    const otherReasonChanged = currentOtherReason !== savedOtherReasonNormalized;
    
    return reasonsChanged || otherReasonChanged;
  }, [projectBanned, blockingReasons, otherReasonText, project])

  const showBlockingReasonsDropdown = useMemo(() => {
    return projectBanned && canChangeBannedStatus;
  }, [projectBanned, canChangeBannedStatus])

  const hasExistingReasons = useMemo(() => {
    const projectAny = project as any;
    return projectAny?.lastBannedInfo?.bannedReasonInfo?.reasons?.length > 0;
  }, [project])

  const hasUnbanRequest = useMemo(() => {
    //@ts-ignore
    return project?.contexts?.includes('unbanrequest');
  }, [project])

  const canSaveNomination = useMemo(() => {
    //@ts-ignore
    return (!!project?.contexts?.find((context: string) => context.startsWith('nomination')) !== projectNominated)
  }, [project, projectNominated])

  const buttonDisabled = disableSaveContexts && !canSaveAgeRestriction && !canSaveBannedStatus && !canSaveBlockingReasons

  useEffect(() => {
    setProjectAgeRestricted(isAgeRestricted);
    if (contexts?.length) {
      const nomination = contexts.find((context: string) => {
        return context.startsWith('nomination')
      })
      setProjectNominated(!!nomination)
      setLastNomination(nomination)
    }
    // setProjectNominated(!!contexts?.includes('nomination'))
  }, [isAgeRestricted, contexts])

  useEffect(() => {
    setProjectBanned(status === 3);
  }, [status])

  const disabledText = useMemo(() => {
    if (!buttonDisabled) {
      return null;
    }

    if (lastApprovesExpert && !canChangeShowCase && canChangeLimb) {
      return t('project_moderated_by_expert');
    }

    if (canChangeShowCase) {
      // if (onHseDesign && selected === 1) return 'Проект уже одобрен';
      if (translateLastApproval === YES && selected === YES) return t('project_already_approved');
      if (translateLastApproval === NO && (isTagModer ? selectedLimb : selected) === NO) return t('project_already_disapproved');
    }

    if (!canChangeShowCase && !lastApprovalLimbIsOwn && moderationInfoLimb) return t('not_your_approve');

    if (translateLastApproval === YES && selectedLimb === YES) return t('project_already_approved');
    if (translateLastApproval === NO && selectedLimb === NO) return t('project_already_disapproved');

    return t('choose_variant');
  }, [moderationInfoLimb, buttonDisabled, canChangeShowCase, translateLastApproval, canChangeLimb, isApproved, lastApprovalLimbIsOwn, onHseDesignTemp, selectedLimb, onHseDesign, selected, t])

  const isMobile = useIsMobile();
  const is1440Width = useBreakpoint(LayoutConst.screen.mac13);
  const [is920Height, setIs920Height] = useState(typeof window !== 'undefined' && window.innerHeight <= LayoutConst.screen.height.projectCover);
  const is1440 = is1440Width || is920Height;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleResize = () => {
      setIs920Height(window.innerHeight <= LayoutConst.screen.height.projectCover);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isPlayerOrMatchRoute) {
      return;
    }
    if (project?.projectId !== id) {
      reloadProject && reloadProject(Date.now())
    }
  }, [project, id, reloadProject, isPlayerOrMatchRoute])

  useEffect(() => {
    if (moderationInfo) {
      setSelected(isApproved ? 1 : 0)
    }
  }, [setSelected, onHseDesign, onHseDesignTemp, moderationInfo, isApproved])
  useEffect(() => {
    if (moderationInfoLimb) {
      setSelectedLimb(isApprovedLimb ? 1 : 0)
    }
  }, [moderationInfoLimb, isApprovedLimb])

  //@ts-ignore
  const projectColor = useMemo(() => (project && project?.additionalInfo?.color), [project])

  const handleSubmitContexts = useCallback(async () => {

    const context = canChangeShowCase ? 'hsedesign' : 'hsedesigntemp'
    // const contextMsg = 'hsedesign';
    const contextMsg = '';

    const updatedContexts = []

    if (canChangeShowCase && canSaveNomination) {
      updatedContexts.push({
        context: projectNominated ? 'nomination' : lastNomination,
        operation: projectNominated ? 1 : 0
      })
    }

    if ((canChangeShowCase ? selected : selectedLimb) !== undefined && !disableSaveContexts) {
      updatedContexts.push({
        context,
        operation: (canChangeShowCase ? selected : selectedLimb)
      })
    }

    if (canSaveBannedStatus || canSaveBlockingReasons) {
      const bannedData = projectBanned && blockingReasons.length > 0 ? {
        reasons: blockingReasons,
        otherReason: blockingReasons.includes(12) ? otherReasonText : ''
      } : null;

      const stringifiedData = bannedData ? JSON.stringify(bannedData) : undefined;

      updatedContexts.push({
        context: 'banned',
        operation: projectBanned ? 1 : 0,
        data: stringifiedData
      })
    }

    const requestBody = {
      projectId: project.projectId,
      contexts: updatedContexts,
      isAgeRestricted: projectAgeRestricted
    };

    await API.request()
        .endpoint(Endpoints.geograffee)
        .url(`/api/Project/UpdateContexts`)
        .post()
        .body(requestBody)
        .send()
        .then((res) => {
          if (res.status === 200) {
            const infoMsgApproved = canChangeShowCase
              ? (selected ? `${t('project_approved_notification')} ${contextMsg}` : `${t('project_disapproved_notification')} ${contextMsg}`)
              : (selectedLimb ? `${t('project_approved_limb_notification')}  ${contextMsg}` : `${t('project_disapproved_limb_notification')} ${contextMsg}`)
            ;

            const infoMsgAge = projectAgeRestricted ? t('project_age_restricted_notification') : t('project_not_age_restricted_notification')

            const infoMsgNominated = projectNominated ? t('project_nominated') : t('project_not_nominated')

            const infoMessageBanned = projectBanned ? t('project_edit.info.project_banned') : t('project_edit.info.project_unbanned')

            if (!canChangeShowCase && selectedLimb !== undefined && !disableSaveContexts) {
              toasts.info(infoMsgApproved);
            }

            if (canChangeShowCase && selected !== undefined && !disableSaveContexts) {
              toasts.info(infoMsgApproved);
            }
            //@ts-ignore
            if (projectAgeRestricted !== !!project?.isAgeRestricted) {
              toasts.info(infoMsgAge)
            }

            if (!!project?.contexts?.includes('nomination') !== projectNominated) {
              toasts.info(infoMsgNominated)
            }

            if (projectBanned !== (project?.status === 3)) {
              toasts.info(infoMessageBanned)
            }
            
            window.location.reload();
          }
          if (res.status === 400) {
            toasts.info(!lastApprovalLimbIsOwn ? t('project_approved_by_other') : t('can_not_do'));
          }
        }).catch((e) => {
          console.error('Ошибка при отправке данных:', e);
          console.error('Error details:', e.response || e);
          toasts.info('Ошибка запроса');
        });
  }, [selected,
    canChangeShowCase,
    canSaveNomination,
    disableSaveContexts,
    project.projectId,
    //@ts-ignore
    project?.isAgeRestricted,
    project?.contexts,
    projectAgeRestricted,
    projectNominated,
    projectBanned,
    lastNomination,
    toasts,
    lastApprovalLimbIsOwn,
  ])

  const toggleAgeRestriction = useCallback(() => {
    setProjectAgeRestricted(!projectAgeRestricted)
  }, [projectAgeRestricted])

  const toggleProjectBan = useCallback(() => {
    const newBlockedState = !projectBanned;
    setProjectBanned(newBlockedState)
    // Не очищаем причины при снятии чекбокса — они должны сохраняться
    
    if (onBlockedStateChange) {
      onBlockedStateChange(newBlockedState, blockingReasons, otherReasonText);
    }
  }, [projectBanned, blockingReasons, otherReasonText, onBlockedStateChange])

  const handleBlockingReasonsChange = useCallback((selectedOptions: readonly any[] | null) => {
    const values = selectedOptions ? selectedOptions.map((option: any) => option.value) : [];
    
    // Ограничение: не более 3 причин
    if (values.length > 3) {
      toasts.info(t('project_edit.max_blocking_reasons_error'));
      return;
    }
    
    setBlockingReasons(values);
    
    if (!values.includes('other') || (values.includes('other') && !otherReasonText.trim())) {
      setOtherReasonText('');
    }
    
    if (onBlockedStateChange && projectBanned) {
      const finalOtherReason = values.includes('other') && otherReasonText.trim() ? otherReasonText : '';
      onBlockedStateChange(projectBanned, values, finalOtherReason);
    }
  }, [otherReasonText, onBlockedStateChange, projectBanned, toasts, t]);

  const handleOtherReasonTextChange = useCallback((ev: any) => {
    const value = ev.target.value;
    // Ограничиваем до 75 символов
    if (value.length <= 75) {
      setOtherReasonText(value);
      
      // Уведомляем родительский компонент об изменении состояния
      if (onBlockedStateChange && projectBanned) {
        onBlockedStateChange(projectBanned, blockingReasons, value);
      }
    }
  }, [onBlockedStateChange, projectBanned, blockingReasons]);

  const toggleNomination = useCallback(() => {
    setProjectNominated(!projectNominated)
    setSelected(YES);
  }, [projectNominated])

  const approvalTitleText = useMemo(() => {
    if (isTagModer && !canChangeShowCase) {
      return t('teacher_text')
    }

    return t('teacher_approved_text')
  } , [isTagModer, t])

  const radioCheckedStatusTrue = useMemo(() => {
    if (disabledText === t('project_moderated_by_expert')) {
      return selected;
    } 
    if (disabledText === t('not_your_approve')) {
      return selectedLimb
    }
    return (canChangeShowCase ? selected : selectedLimb) === YES
  } , [selected, selectedLimb, disabledText])

  const radioCheckedStatusFalse = useMemo(() => {
    if (disabledText === t('project_moderated_by_expert')) {
      return !selected;
    } 
    if (disabledText === t('not_your_approve')) {
      return !selectedLimb
    }
    return (canChangeShowCase ? selected : selectedLimb) === NO
  } , [selected, selectedLimb, disabledText])

  const onRadioStatusChange = useCallback((type: number) => {
    if (
      disabledText === t('project_moderated_by_expert') || 
      disabledText === t('not_your_approve')) {
      return;
    }

    if (type === YES) {
      canChangeShowCase ? setSelected(YES) : setSelectedLimb(YES);
      try {
        window.dispatchEvent(new CustomEvent('approval-radio-change', { 
          detail: { target: canChangeShowCase ? 'main' : 'limb', value: YES } 
        }));
      } catch {}
      return;
    }

    canChangeShowCase ? setSelected(NO) : setSelectedLimb(NO);
    try {
      window.dispatchEvent(new CustomEvent('approval-radio-change', { 
        detail: { target: canChangeShowCase ? 'main' : 'limb', value: NO } 
      }));
    } catch {}
  } , [canChangeShowCase, disabledText, t])

  if (!showModal) {
    return null;
  }

  const Radio = (props: any) => {
   
    
    return (
      <CheckBoxWrapper disabled={props.disabled}>
        <FlexBox>
          <RadioInputSource {...props as any} type={'radio'}/>
          <RadioBox {...props as any}>
            <RadioCircle {...props as any} />
            <RadioLabel {...props as any} >
              {props.title}
            </RadioLabel>
          </RadioBox>
        </FlexBox>
      </CheckBoxWrapper>
    )
  }

  return (
    <FlexBox direction="column" directionMobile="column" width="100%" gy={16} gyMobile={8}>
      <ApprovalDialogWrapper
        className='approval-dialog-wrap'
        width={'100%'}
        direction="column"
        align={"center"}
        justify={"center"}
        offsetLeft={!isMobile && !fullWidth ? 24 : 0}
        isProjectPage={isProjectPage}
        wide={wide}
        fullWidth={fullWidth}
        showBlockingReasons={showBlockingReasonsDropdown}
      >
        <FlexBox className={'left-column-container'} width="100%">
        {canChangeShowCase && <ContentWrapper offsetBottomMobile={4} align={"center"} justify={"space-between"} direction={"row"} gy={0}
                                              isProjectPage={isProjectPage}>
          <FlexBox direction="column" directionMobile="column" width="100%">
            <FlexBox align="center" alignMobile="center" justifyMobile="flex-start" directionMobile='row'
                     justify='flex-start' gyMobile={8}>
              <ApprovalTitle
                isProjectPage={isProjectPage}
                color={theme.projectView.titleColor}>
                {approvalTitleText}
              </ApprovalTitle>

              <RadioButtonWrapper isProjectPage={isProjectPage}>
                <Radio
                  onChange={() => false}
                  name={`approval-readonly-${isBottomMenu ? 'bottom' : 'top'}-${isProjectPage ? 'project' : 'main'}`}
                  title={t('yes')}
                  checked={lastLimbStatus}
                  clr={getApprovalFontColor(projectColor)}
                  disabled
                  approval
                  firstRadioButton
                />
                <Radio
                  onChange={() => false}
                  name={`approval-readonly-${isBottomMenu ? 'bottom' : 'top'}-${isProjectPage ? 'project' : 'main'}`}
                  title={t('no')}
                  checked={lastHSEApproveLimbIndex !== -1 && !lastLimbStatus }
                  clr={getApprovalFontColor(projectColor)}
                  disabled
                  approval
                />
              </RadioButtonWrapper>
            </FlexBox>
          </FlexBox>
        </ContentWrapper>}
        {(canChangeShowCase || canChangeLimb) && <ContentWrapper offsetBottomMobile={4} align={"center"} justify={"space-between"} direction={"row"} gy={0}
                        isProjectPage={isProjectPage}>
          <FlexBox direction="column" directionMobile="column" width="100%">
            <FlexBox align="center" alignMobile='center' justify='flex-start' justifyMobile="flex-start"
                     directionMobile='row' gyMobile={8}>
              <ApprovalTitle
                isProjectPage={isProjectPage}
                color={theme.projectView.titleColor}>
                {t('teacher_text')}
              </ApprovalTitle>
              <RadioButtonWrapper isProjectPage={isProjectPage} data-tip={disabledText}>
                <Radio
                  onChange={() => onRadioStatusChange(YES)}
                  name={`approval-${isBottomMenu ? 'bottom' : 'top'}-${isProjectPage ? 'project' : 'main'}`}
                  title={t('yes')}
                  checked={radioCheckedStatusTrue}
                  clr={getApprovalFontColor(projectColor)}
                  approval
                  firstRadioButton
                />
                <Radio
                  onChange={() => onRadioStatusChange(NO)}
                  name={`approval-${isBottomMenu ? 'bottom' : 'top'}-${isProjectPage ? 'project' : 'main'}`}
                  title={t('no')}
                  checked={radioCheckedStatusFalse}
                  clr={getApprovalFontColor(projectColor)}
                  approval
                />
              </RadioButtonWrapper>
            </FlexBox>
          </FlexBox>
        </ContentWrapper>}
        <ContentWrapper align={"center"} justify={"space-between"} direction={"row"} gy={0}
                        isProjectPage={isProjectPage}>
          <FlexBox direction="column" directionMobile="column" width="100%">
            <CheckboxHolder isBottomMenu={isBottomMenu}>
              <MagicBoxRow 
                onClick={toggleAgeRestriction}
                checked={projectAgeRestricted}
                label={t('project_edit.age_restricted')}
              />
              {/* <CheckboxField
                onClick={toggleAgeRestriction}
                checked={projectAgeRestricted}
                label={t('project_edit.age_restricted')}
                dark

              /> */}
            </CheckboxHolder>
          </FlexBox>
        </ContentWrapper>
        {canChangeShowCase && <ContentWrapper align={"center"} justify={"space-between"} direction={"row"} gy={0}
                                              isProjectPage={isProjectPage}>
          <FlexBox direction="column" directionMobile="column" width="100%">
            <CheckboxHolder isBottomMenu={isBottomMenu}>
              <MagicBoxRow 
                onClick={toggleNomination}
                checked={projectNominated}
                label={t('project_edit.nominate_project')}
              />
              {/* <CheckboxField
                onClick={toggleNomination}
                checked={projectNominated}
                label={t('project_edit.nominate_project')}
                dark

              /> */}
            </CheckboxHolder>
          </FlexBox>
        </ContentWrapper>}
        {canChangeBannedStatus && (
          <ContentWrapper align={"center"} justify={"space-between"} direction={"row"} gy={0}
                          isProjectPage={isProjectPage}>
            <FlexBox direction="column" directionMobile="column" width="100%">
              <CheckboxHolder isBottomMenu={isBottomMenu}>
                <MagicBoxRow 
                  onClick={toggleProjectBan}
                  checked={projectBanned}
                  label={t('project_edit.project_banned')}
                />
              {/* <CheckboxField
                  onClick={toggleProjectBan}
                  checked={projectBanned}
                  label={t('project_edit.project_banned')}
                  dark
                /> */}
              </CheckboxHolder>
              {showBlockingReasonsDropdown && (
                <BlockingReasonsWrapper showBlockingReasons={showBlockingReasonsDropdown}>
                  {hasUnbanRequest ? (
                    <>
                      <BlockingReasonsLabel>
                        {t('project_edit.select_blocking_reason')}
                      </BlockingReasonsLabel>
                      <BlockingReasonsSelect
                        options={[]}
                        value={[{ value: -1, label: t('project_edit.project_under_review') }]}
                        placeholder={t('project_edit.project_under_review')}
                        isOptionDisabled={() => true}
                        onChange={() => {}}
                        isDisabled={true}
                        theme={theme}
                        isMobile={isMobile}
                        is1440={is1440}
                      />
                    </>
                  ) : (
                    <>
                      <BlockingReasonsLabel>
                        {t('project_edit.select_blocking_reason')} ({blockingReasons.length}/3)
                      </BlockingReasonsLabel>
                      <BlockingReasonsSelect
                        options={BLOCKING_REASONS.map(r => ({ value: r.value, label: t(r.labelKey) }))}
                        value={BLOCKING_REASONS
                          .filter(r => blockingReasons.includes(r.value))
                          .map(r => ({ value: r.value, label: t(r.labelKey) }))
                        }
                        placeholder={t('project_edit.select_blocking_reason_placeholder')}
                        isOptionDisabled={(option: any) => !blockingReasons.includes(option.value) && blockingReasons.length >= 3}
                        onChange={handleBlockingReasonsChange}
                        isDisabled={hasExistingReasons}
                        theme={theme}
                        isMobile={isMobile}
                        is1440={is1440}
                      />
                      {blockingReasons.includes(12) && (
                        <OtherReasonInputWrapper>
                          <BlockingReasonsLabel>
                            {t('project_edit.other_reason_description')} ({otherReasonText.length}/75)
                          </BlockingReasonsLabel>
                          <OtherReasonInput
                            value={otherReasonText}
                            onChange={handleOtherReasonTextChange}
                            placeholder={t('project_edit.select_blocking_reason_placeholder')}
                            maxLength={75}
                            disabled={hasExistingReasons}
                          />
                        </OtherReasonInputWrapper>
                      )}
                    </>
                  )}
                </BlockingReasonsWrapper>
              )}
            </FlexBox>
          </ContentWrapper>
        )}
        </FlexBox>
        <ButtonHolder>
          <div data-tip={disabledText}>
            <ApproveProjectButton
              isProjectPage={isProjectPage}
              onClick={handleSubmitContexts}
              disabled={disableSaveContexts && !canSaveAgeRestriction && !canSaveNomination && !canSaveBannedStatus && !canSaveBlockingReasons}
            >
              {t('save')}
            </ApproveProjectButton>
          </div>
        </ButtonHolder>
        <ReactTooltip/>
      </ApprovalDialogWrapper>
    </FlexBox>
  );
};

export default memo(ApprovalDialog)
import React, { FC, memo, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Colors, FInter, FlexBox, MagicBox, useIsMobile, useBreakpoint, useToasts, MagicBoxRow } from '@brg/ui';
import { useTranslation } from 'react-i18next';
import styled, { useTheme, css } from 'styled-components';
import { Input } from '@brg/input';
import { API, Endpoints, Profile, Project, ProjectAPI } from '@brg/api';
import { ProfileRole, useProfile } from "@brg/auth";
import { CheckBoxProps } from '@brg/input/dist/checkbox/InputCheckbox';
import { bProfile } from "../../../../core/utils/bProfile";
import ReactTooltip from "react-tooltip";
import { RectangularButtonFilled } from "shared/components/buttons";
import { LayoutConst } from "../../../../core";
import { AppTester } from 'supporting';
import { BilingLabel } from "../../projectEdit/styles";
import { HsePolicies } from 'shared/constants/HsePolicies';
import { ExpertsTagIdsByPolicy, getExpertTags, HasTagExpertPolicy, HasTagModerPolicy, isExpert, isExpertByPolicy, isTagExpert, ModeratedTagsByPolicy } from 'shared/helpers/utils/TagsUtils';
import { useGetFriendlyUrlParam } from 'shared/hooks/useGetFriendlyUrlParam';
import { useRouteMatch } from 'react-router-dom';
import { BlockingReasonsSelect } from './BlockingReasonsSelect/BlockingReasonsSelect';

const YES = 1;
const NO = 0;

// Причины блокировки контента - используем enum значения из бэкенда
export const BLOCKING_REASONS = [
  { value: 0, labelKey: 'project_edit.blocking_reason_adult_content' },
  { value: 1, labelKey: 'project_edit.blocking_reason_hatred_incitement' },
  { value: 2, labelKey: 'project_edit.blocking_reason_criticism_insults' },
  { value: 3, labelKey: 'project_edit.blocking_reason_disinformation' },
  { value: 4, labelKey: 'project_edit.blocking_reason_personal_information' },
  { value: 5, labelKey: 'project_edit.blocking_reason_suicide_drugs' },
  { value: 6, labelKey: 'project_edit.blocking_reason_violence_threats' },
  { value: 7, labelKey: 'project_edit.blocking_reason_extremism' },
  { value: 8, labelKey: 'project_edit.blocking_reason_prohibited_goods' },
  { value: 9, labelKey: 'project_edit.blocking_reason_impersonation' },
  { value: 10, labelKey: 'project_edit.blocking_reason_intellectual_property' },
  { value: 11, labelKey: 'project_edit.blocking_reason_spam' },
  { value: 12, labelKey: 'project_edit.blocking_reason_other' }
];

type ApprovalDialogProps = {
  project: Project;
  onShouldReload?: (project: Project) => void;
  reloadProject?: (time: any) => void;
  isProjectPage?: boolean;
  isBottomMenu?: boolean;
  wide?: boolean;
  fullWidth?: boolean;
  onBlockedStateChange?: (isBlocked: boolean, reasons: number[], otherReason: string) => void;
};

export const CheckBoxWrapper = styled(FlexBox)<{ disabled: boolean }>`
  align-items: center;
  position: relative;
  cursor: ${({ disabled }) => disabled ? 'default' : 'pointer'};
  user-select: none;

`;

export const ApproveTitle = styled(FlexBox)`
  width: 100%;
  justify-content: flex-start;
  font-size: 17px;
  line-height: 22px;
  letter-spacing: -0.34px;
  font-weight: 600;
  color: ${Colors.galBlack73};
  padding-left: 150px;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    font-size: 15px;
    font-style: normal;
    font-weight: 600;
    line-height: 20px; /* 133.333% */
    letter-spacing: -0.3px;
    padding-left: 135px;
  }

  @media (max-width: ${LayoutConst.screen.md}px) {
    padding-left: 0px;
  }

`;

export const ApprovalDialogWrapper = styled(FlexBox)<{ isProjectPage?: boolean, wide?: boolean, fullWidth?: boolean, showBlockingReasons?: boolean }>`
  background-color: ${({ theme }) =>
    AppTester.isZelandTheme() ? Colors.zelWhite10 : theme.mainDropdownStyles.bg};
  border-radius: 8px;
  width: ${({ fullWidth }) => fullWidth ? '100%' : 'calc(100% - 24px)'};
  padding: 25px 24px;
  margin-top: 0;
  justify-content: space-between;
  align-items: flex-start;
  margin-left: ${({ fullWidth }) => fullWidth ? '0' : '24px'};
  position: relative;
  // gap: 16px;

  .left-column-container {
    gap: 16px;
    flex-flow: column;

    @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
      gap: 12px;
    }
  }

  ${({ wide }) => wide && css`
    flex-flow: row;
    align-items: flex-end;

    ${ButtonHolder} {
      width: min-content;
      button {
        margin-left: 0;
      }
    }

    .left-column-container {
      width: 100%;
      max-width: 373px;
    }

    ${ApprovalTitle} {
      width: 204px;
      white-space: nowrap;
    }
  `};
  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    width: ${({ fullWidth }) => fullWidth ? '100%' : 'calc(100% - 24px)'};
    margin-left: ${({ fullWidth }) => fullWidth ? '0' : '24px'};
    margin-top: 0;
    padding: 16px;

    ${({ wide }) => wide && css`
      flex-flow: row;
      align-items: flex-end;
      position: relative;
      flex-direction: column;

      ${ButtonHolder} {
        // position: absolute;
        bottom: 16px;
        right: 16px;
      }

      .left-column-container {
        gap: 16px;
        width: 100%;
      }

      ${ApprovalTitle} {
        width: 140px;
        white-space: pre-line;
      }
  `};

  }
  @media (max-width: ${LayoutConst.screen.md}px) {
    margin-left: ${({ fullWidth }) => fullWidth ? '0' : '12px'};
    padding: 16px;
    width: ${({ fullWidth }) => fullWidth ? '100%' : 'calc(100% - 12px)'};
  }
  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    margin-top: 0;
    margin-left: ${({ fullWidth }) => fullWidth ? '0' : undefined};
    width: ${({ fullWidth }) => fullWidth ? '100%' : undefined};
  }

  ${({ isProjectPage }) => isProjectPage ? css`
    @media (max-width: ${LayoutConst.screen.md}px) {
      min-width: 203px;
      padding: 12px;
      gap: 12px;
    }
    @media (max-width: 780px) {
      width: 100%;
    }
  ` : null};

  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    margin-left: 0px;
    width: 100%;
    .left-column-container {
      gap: 12px;
      width: 100%;
    }
  }

  ${({ showBlockingReasons }) => showBlockingReasons && css`
    @media (max-width: ${LayoutConst.screen.s}px) {
      flex-flow: column;
      align-items: stretch;

      ${ButtonHolder} {
        width: 100%;

        div {
          width: 100%;
        }
      }
    }

    @media (min-width: ${LayoutConst.screen.s + 1}px) {
      flex-flow: row;
      align-items: flex-end;
      gap: 16px;

      .left-column-container {
        flex: 1;
        min-width: 0;
      }

      ${ButtonHolder} {
        width: auto;
        flex-shrink: 0;
      }
    }
  `}
`;

export const ContentWrapper = styled(FlexBox)<{ isProjectPage?: boolean }>`
  width: 100%;

  ${BilingLabel} {
    font-size: 15px !important;
    line-height: 20px !important;

    @media (max-width: ${LayoutConst.screen.m}px) {
      font-size: 13px !important;
      line-height: 16px !important;
    }
  }

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
  }

  ${({ isProjectPage }) => isProjectPage ? css`

    @media (max-width: ${LayoutConst.screen.md}px) {
      flex-direction: column;
      justify-content: center;
    }

    @media (max-width: ${LayoutConst.screen.s - 1}px) {
      flex-direction: row;
    }
  ` : null};
`;

export const RadioButtonWrapper = styled(FlexBox)<{ isProjectPage?: boolean }>`

  ${({ isProjectPage }) => isProjectPage ? css`
    @media (max-width: ${LayoutConst.screen.md}px) {
      margin-top: 0 !important
    }

    @media (max-width: ${LayoutConst.screen.s - 1}px) {
      margin-bottom: 0;
    }
  ` : null};
`;

export const RadioBox = styled.div<{ isBook?: boolean, approval?: boolean, firstRadioButton?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-direction: ${p => p.isBook ? "column" : "row"};

  :first-of-type {
    margin-right: 16px;
    @media (max-width: ${LayoutConst.screen.md}px) {
    ${({approval, firstRadioButton}) => approval && !firstRadioButton && css`
    margin-right: 0px;
  `}
  
  }
  }
`

export const RadioCircle = styled(FlexBox)<CheckBoxProps & { clr?: string }>`
  width: 24px;
  height: 24px;
  background: ${p => p.theme.approvalBlockStyles.radioCircle};
  border: 1px solid ${p => p.clr ? p.clr : '#B6B6B5'};
  box-sizing: border-box;
  border-radius: 12px;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  svg {
    opacity: 0;
    transition: all 0.2s;
  }

  opacity: ${p => p.disabled ? 0.31 : 1};
`;

export const RadioLabel = styled(FInter.SmallTextBold)<{ clr?: string }>`
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 129.412% */
  letter-spacing: -0.34px;
  color: ${p => p.clr ? p.clr : '#545453'};

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    font-size: 13px;
    font-style: normal;
    line-height: 16px; /* 133.333% */
    letter-spacing: -0.3px;
  }
    
`

export const ApproveProjectButton = styled(RectangularButtonFilled)<{ isBook?: boolean, isProjectPage?: boolean }>`
  font-size: 17px;
  line-height: 22px;
  letter-spacing: -0.02em;
  font-weight: 600;
  padding: 4px 32px;
  height: 40px !important;
  min-width: 85px;
  margin-left: ${p => p.isBook ? "0px" : "9.5px"};
  max-width: 127px;
  color: ${({theme}) => theme.approvalBlockStyles.buttonColor};
  background: ${({theme}) => theme.approvalBlockStyles.buttonBg};
  margin-top: 8px;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    font-size: 15px;
    line-height: 22px;
    letter-spacing: normal;
    font-weight: 700;
    //height: 24px;
    margin-left: 0;
    min-width: 94px;
    width: 100% !important;
    max-width: initial !important;
  }
  @media (max-width: ${LayoutConst.screen.md}px) {
    margin-top: 0;
    min-width: 103px;
    //width: 103px;
  }
  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    min-width: 143px;
    //width: 143px;
  }


    /*    @media(min-width: 1920px) {
      margin-left: ${p => p.isBook ? "0px" : "20px"};
      font-size: 13px;
      line-height: 18px;
      padding: 6px 16px;
      height: 30px;
      min-width: 103px;
    }*/

  ${({ isProjectPage, isBook }) => isProjectPage ? css`
    @media (max-width: ${LayoutConst.screen.md}px) {
      margin-left: 0;
    }

    @media (max-width: ${LayoutConst.screen.s - 1}px) {
      margin-left: 0;
    }
  ` : null};
`;

export const RadioInputSource = styled.input`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 100;
  height: 100%;
  cursor: ${({ disabled }) => disabled ? 'default' : 'pointer'};
  opacity: 0;

  &:checked {
    & ~ ${RadioBox} {

      ${RadioCircle} {
        border: 1px solid ${Colors.galBlack47};

        :after {
          content: "";
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: ${p => p.theme.approvalBlockStyles.radioDot};
        }
      }

      // ${RadioLabel} {
      //   color: #545453;
      // }
    }
`;

export const ApprovalTitle = styled(FInter.SmallTextBold)<{ isBook?: boolean, isProjectPage?: boolean }>`
  font-size: 15px;
  line-height: 20px; /* 129.412% */
  letter-spacing: -0.34px;
  font-weight: 400;
  color: ${Colors.galBlack47};
  margin-right: ${p => p.isBook ? "0px" : "24px"};
  width: 231px;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    font-size: 15px;
    line-height: 20px;
    font-style: normal;
    width: 140px;

    line-height: 20px; /* 133.333% */
    letter-spacing: -0.3px;
    margin-right: ${p => p.isBook ? "0px" : "24px"};
  }

  @media (max-width: ${LayoutConst.screen.md}px) {
    width: 100%;
    font-size: 13px;
    line-height: 16px;
    letter-spacing: -0.26px;
  }

  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    width: auto;
  }


  ${({ isProjectPage, isBook }) => isProjectPage ? css`
    @media (max-width: ${LayoutConst.screen.md}px) {
      margin-bottom: 0px;
      margin-right: 8px;
      width: 100%;
    }

    @media (max-width: ${LayoutConst.screen.s - 1}px) {
      margin-bottom: 0;
      margin-right: ${isBook ? "0px" : "40px"};
    }
  ` : null};
`;

export const getApprovalFontColor = (clr: string) => {
  const list = ["#FFFFFF", "#D52B1E", "#FFC19C", "#FFBC3D", "#BED600", "#3D7EDB", "#AACAE6", "#F5EC5A", "#000000", "#D1D4D3", "#5E6A71", "#E2DDCB", "#AEA79F", "#BCD473", "#394A58"];
  const list_1 = [list[1], list[2], list[3], list[4], list[6], list[8], list[9], list[11], list[13]];

  return !list_1.includes(clr) ? Colors.galBlack47 : Colors.galBlack21;
  // #909090 - темно серый
  // ##CDCDCD - серый
}

const CheckboxHolder = styled(FlexBox)<{ isBottomMenu?: boolean }>`

  ${({ isBottomMenu }) => isBottomMenu && css`
    div > {
      // :first-child {
      //   background-color: #fefefe !important;
      // }
    }
  `};

  ${BilingLabel} {
    font-size: 17px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px; /* 129.412% */
    letter-spacing: -0.34px;
    color: ${Colors.galBlack47};

    @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
      font-size: 15px;
      font-style: normal;
      line-height: 20px; /* 133.333% */
      letter-spacing: -0.3px;
    }
    @media (max-width: ${LayoutConst.screen.md}px) {
     font-size: 13px;
      line-height: 16px; /* 123.077% */
      letter-spacing: -0.26px;
    }
  }
`

const ButtonHolder = styled(FlexBox)`
  width: 100%;
  justify-content: end;
  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    width: 100%;

    div {
      width: 127px;
    }
  }

  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    div {
      width: 100%;
    }
  }
`

const BlockingReasonsWrapper = styled(FlexBox)<{ showBlockingReasons?: boolean }>`
  flex-direction: column;
  width: 100%;
  margin: 16px 0;
  position: relative;
  z-index: 100;

  ${({ showBlockingReasons }) => showBlockingReasons && css`
    margin: 16px 0 0 0;
  `}

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    margin: 16px 0 0 0;
  }
`;

const BlockingReasonsLabel = styled(FInter.SmallTextBold)`
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.34px;
  color: ${Colors.galBlack47};
  margin-bottom: 8px;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    font-size: 13px;
    line-height: 16px;
    letter-spacing: -0.3px;
  }
`;

const OtherReasonInputWrapper = styled(FlexBox)`
  flex-direction: column;
  margin-top: 8px;
`;

const OtherReasonInput = styled(Input.Text)`
  max-width: 100%;
  border: none !important;
  color: ${p => p.theme.themeName === 'blackTheme' || p.theme.themeName === 'greyTheme' || p.theme.themeName === 'jhonataTheme' ? '#FFFFFF' : p.theme.dropdown.textColor} !important;
  background-color: ${p => p.theme.approvalBlockStyles?.selectBg ?? p.theme.dropdown.bgColor} !important;
  
  &:focus {
    border: none !important;
    outline: none !important;
    color: ${p => p.theme.themeName === 'blackTheme' || p.theme.themeName === 'greyTheme' || p.theme.themeName === 'jhonataTheme' ? '#FFFFFF' : p.theme.dropdown.textColor} !important;
  }
  
  &:focus-visible {
    outline: none !important;
  }
  
  input {
    padding-left: 8px !important;
    color: ${p => p.theme.themeName === 'blackTheme' || p.theme.themeName === 'greyTheme' || p.theme.themeName === 'jhonataTheme' ? '#FFFFFF' : p.theme.dropdown.textColor} !important;
    background-color: ${p => p.theme.approvalBlockStyles?.selectBg ?? p.theme.dropdown.bgColor} !important;
    
    &::placeholder {
      transition: none !important;
      color: ${p => p.theme.themeName === 'blackTheme' || p.theme.themeName === 'greyTheme' || p.theme.themeName === 'jhonataTheme' ? '#FFFFFF' : p.theme.dropdown.textColor} !important;
      opacity: 0.7 !important;
    }
    
    &:hover::placeholder {
      opacity: 0.7 !important;
      color: ${p => p.theme.themeName === 'blackTheme' || p.theme.themeName === 'greyTheme' || p.theme.themeName === 'jhonataTheme' ? '#FFFFFF' : p.theme.dropdown.textColor} !important;
    }
  }

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    height: 40px !important;
  }
`;


const ApprovalDialog: FC<ApprovalDialogProps> = ({
  project: initialProject,
  reloadProject,
  isProjectPage,
  isBottomMenu,
  wide,
  fullWidth,
  onBlockedStateChange
}) => {
  const { t } = useTranslation();
  const profile = useProfile();
  const theme = useTheme();
  const id = useGetFriendlyUrlParam("id");
  const isPlayerOrMatchRoute = Boolean(useRouteMatch(['/player/:id', '/match/:id']));
  const toasts = useToasts();
  const [project, setProject] = useState(initialProject)
  const [projectAgeRestricted, setProjectAgeRestricted] = useState<boolean>(false);
  const [projectBanned, setProjectBanned] = useState<boolean>(false);
  const [projectNominated, setProjectNominated] = useState<boolean>(false)
  const [lastNomination, setLastNomination] = useState<string | undefined>()
  const [blockingReasons, setBlockingReasons] = useState<number[]>([]);
  const [otherReasonText, setOtherReasonText] = useState<string>('');
  const reasonsLoadedRef = useRef<string | null>(null);
  
  useEffect(() => {
    const projectAny = project as any;
    const projectId = project?.projectId;
    
    if (projectId && reasonsLoadedRef.current !== projectId) {
      if (project?.status === 3 && projectAny?.lastBannedInfo?.bannedReasonInfo) {
        const { reasons, otherReason } = projectAny.lastBannedInfo.bannedReasonInfo;
        
        if (reasons && Array.isArray(reasons)) {
          setBlockingReasons(reasons);
          
          const otherReasonValue = otherReason || '';
          setOtherReasonText(otherReasonValue);
          
          reasonsLoadedRef.current = projectId;
        }
      } else {
        if (reasonsLoadedRef.current !== projectId) {
          setBlockingReasons([]);
          setOtherReasonText('');
          reasonsLoadedRef.current = projectId;
        }
      }
    }
  }, [project]);
  
  const isTagExpert = isExpert(profile, project?.tagsID)
  const projectTagAndGroupIds = [
    ...(project?.tagsID || []),
    ...(project?.tags?.flatMap((tag: any) => tag?.groupIds || []) || []),
  ];
  const isTagExpertByPolicy = isExpertByPolicy(profile, projectTagAndGroupIds);


  const hasApproveProjectLimb = (profile: Profile<"Local"> | undefined) => profile && profile.rolesList && Array.isArray(profile.rolesList)
    && (new ProfileRole(HsePolicies.can_approve_project_limb)).containsIn(profile.rolesList);

  const isJuryMember = (profile: Profile<"Local"> | undefined) => profile && profile.rolesList && Array.isArray(profile.rolesList)
    && (new ProfileRole(HsePolicies.is_jury_member)).containsIn(profile.rolesList);

  const isTagModer = useMemo(() => {
    if (profile) {
      const { tags: projectTags = [] } = project;
      // @ts-ignore
      const { tagListId: moderatedTags } = profile;
      // @ts-ignore
      const isTagModerResult = moderatedTags.length && projectTags && projectTags.some(({
        tagId,
        // @ts-ignore
        isEducationTag
      }) => isEducationTag && (moderatedTags.includes(tagId)));

      if (isTagModerResult) {
        return isTagModerResult;
      }

      if (hasApproveProjectLimb(profile)) {
        return true;
      }

      if (!HasTagModerPolicy(profile)) {
        return false;
      }

      const moderatedTagsByPolicy = ModeratedTagsByPolicy(profile);

      const isTagModerByPolicy = projectTags && projectTags.some(({
        tagId,
        // @ts-ignore
        isEducationTag
      }) => isEducationTag && moderatedTagsByPolicy?.includes(tagId));

      return isTagModerByPolicy;
    }
    return false;
  }, [project, profile]);

  const isTagModer_Expert = useMemo(() => {
    if (profile) {
      const { tags: projectTags = [] } = project;
      // @ts-ignore
      const { tagsExpert: moderatedTags_Expert } = profile;

      // @ts-ignore
      const isTagModerExpert = isTagExpert && moderatedTags_Expert?.length && projectTags && projectTags.some(({
        tagId,
        isEducationTag
      }) => /*isEducationTag &&*/ getExpertTags(moderatedTags_Expert).includes(tagId));

      if (isTagModerExpert) {
        return isTagModerExpert;
      }

      if (!HasTagExpertPolicy(profile)) {
        return false;
      }

      const moderatedTagsExpertByPolicy = ExpertsTagIdsByPolicy(profile);

      const isTagModerExpertByPolicy = isTagExpertByPolicy && projectTags && projectTags.some(({
        tagId,
        groupIds = [],
      }: any) => moderatedTagsExpertByPolicy?.includes(tagId)
        || groupIds.some((groupId: string) => moderatedTagsExpertByPolicy?.includes(groupId)));

      return isTagModerExpertByPolicy;
    }
    return false;
  }, [project, profile, isTagExpertByPolicy]);

  const canChangeShowCase = useMemo(() => {
    if (bProfile.isModer(profile)) return true;
    if (bProfile.isMiddleModer(profile)) return true;
    if (isTagModer_Expert && (bProfile.isExpertModer(profile) || isJuryMember(profile))) return true;
    if (profile && profile?.rolesList && Array.isArray(profile?.rolesList)
      && (new ProfileRole(HsePolicies.can_approve_project_main)).containsIn(profile?.rolesList || [])) return true;

    return false;
  }, [isTagModer_Expert, isJuryMember, profile])

  const canChangeLimb = useMemo(() => {
    if (canChangeShowCase) return true;
    if (bProfile.isJuniorModer(profile)) return true;
    if (isTagModer) return true;
    if (profile && profile?.rolesList && Array.isArray(profile?.rolesList)
      && (new ProfileRole(HsePolicies.can_approve_project_limb)).containsIn(profile?.rolesList || [])) return true;

    return false;
  }, [canChangeShowCase, isTagModer, profile])

  const canChangeBannedStatus = useMemo(() => {
    if (bProfile.isModer(profile)) return true;
    if (profile && profile?.rolesList && Array.isArray(profile?.rolesList)
      && (new ProfileRole(HsePolicies.can_change_banned_status_project)).containsIn(profile?.rolesList || [])) return true;

    return false;
  }, [project, profile])

  const onHseDesign = useMemo(() => !!project.contexts?.includes('hsedesign'), [project]);
  const onHseDesignTemp = useMemo(() => !!project.contexts?.includes('hsedesigntemp'), [project]);

  const isChapter = useMemo(() => (id === project?.projectId && !!project?.book), [project, id])

  const [selected, setSelected] = useState<number | undefined>();
  const [selectedLimb, setSelectedLimb] = useState<number | undefined>();

  useEffect(() => {
    const handleApprovalChange = (event: CustomEvent) => {
      const { target, value } = event.detail;
      if (target === 'limb') {
        setSelectedLimb(value);
      } else if (target === 'main') {
        setSelected(value);
      }
    };

    window.addEventListener('approval-radio-change', handleApprovalChange as any);
    
    return () => {
      window.removeEventListener('approval-radio-change', handleApprovalChange as any);
    };
  }, []);

  // @ts-ignore
  const { approvals, isAgeRestricted, contexts, status } = project;

  const lastApprovals = [...(approvals || [])].reverse()

  // @ts-ignore
  const sortedLastApprovals = lastApprovals.sort((a, b) => new Date(b?.date) - new Date(a?.date))
  const lastHSEApproveLimbIndex = sortedLastApprovals.findIndex((approve: any) => ['hsedesigntemp'].includes(approve.contextService));
  const lastHSEApproveIndex = sortedLastApprovals.findIndex((approve: any) => ['hsedesign'].includes(approve.contextService));

  const moderationInfo = useMemo(() => {
    if (lastHSEApproveIndex !== -1 && lastHSEApproveLimbIndex !== -1) {
      return sortedLastApprovals[lastHSEApproveIndex];
    }
    return (lastHSEApproveIndex === -1 || onHseDesignTemp) ? undefined : sortedLastApprovals[lastHSEApproveIndex]
  }, [lastHSEApproveIndex, lastHSEApproveLimbIndex, onHseDesignTemp, sortedLastApprovals])

  const moderationInfoLimb = useMemo(() => {

    // return (lastHSEApproveLimbIndex === -1 || (lastHSEApproveIndex < lastHSEApproveLimbIndex)) ? undefined : sortedLastApprovals[lastHSEApproveLimbIndex]
    return /*(lastHSEApproveLimbIndex === -1 || onHseDesign) ? undefined : */sortedLastApprovals[lastHSEApproveLimbIndex]
  }, [lastHSEApproveLimbIndex, onHseDesign, sortedLastApprovals])

  const { lastApprovalIsOwn, lastApprovalLimbIsOwn, lastApprovesExpert } = useMemo(() => {

    if (!canChangeLimb) {
      return {
        lastApprovalIsOwn: false,
        lastApprovalLimbIsOwn: false,
      };
    }

    return {
      lastApprovalIsOwn: Boolean(!!moderationInfo && moderationInfo?.accountId === profile?.accountId),
      lastApprovalLimbIsOwn: Boolean(!!moderationInfoLimb && moderationInfoLimb?.accountId === profile?.accountId),
      lastApprovesExpert: (
        moderationInfo?.role?.includes('expertmoder') || moderationInfo?.role?.includes('moder') || 
        moderationInfo?.role?.includes('supermoder') || moderationInfo?.role?.includes('middlemoder'))  && 
        !canChangeShowCase && moderationInfo && moderationInfo.accountId !== profile?.accountId
    }
  }, [canChangeLimb, moderationInfo, profile?.accountId, moderationInfoLimb, canChangeShowCase]);

  const showModal = useMemo(() => {
    if (isChapter) return false;
    return canChangeShowCase || canChangeLimb || canChangeBannedStatus

  }, [isChapter, canChangeShowCase, canChangeLimb])

  const lastLimbStatus = useMemo(
    () => (sortedLastApprovals?.find(
      ({ contextService }) => contextService === 'hsedesigntemp')?.isApproved)
    , [sortedLastApprovals])

  useEffect(() => {
    if (selectedLimb === undefined && lastLimbStatus !== undefined) {
      setSelectedLimb(lastLimbStatus ? YES : NO);
    }
  }, [selectedLimb, lastLimbStatus]);

  const isApproved = useMemo(() => {
    return moderationInfo?.isApproved
  }, [moderationInfo])
  const isApprovedLimb = useMemo(() => {
    return moderationInfoLimb?.isApproved
  }, [moderationInfoLimb])

  const canApprove = useMemo(() => {
    if (isApproved) return false
    if (!canChangeShowCase) return false

    return true
  }, [isApproved, canChangeShowCase])
  const canApproveLimb = useMemo(() => {
    if (isApprovedLimb) return false
    if (!canChangeLimb) return false
    if (!lastApprovalLimbIsOwn && moderationInfoLimb) return false

    return true
  }, [isApprovedLimb, canChangeLimb, lastApprovalLimbIsOwn])

  const translateLastApproval = useMemo(
    () => {
      const currentApproveState = (
        canChangeShowCase ? moderationInfo?.isApproved : moderationInfoLimb?.isApproved
      );

      if (currentApproveState === undefined) {
        return undefined
      }

      return !!currentApproveState ? YES : NO
    }
    , [moderationInfo, canChangeShowCase, moderationInfoLimb])

  const disableSaveContexts = useMemo(() => {

      if (lastApprovesExpert && !canChangeShowCase && canChangeLimb) return true;

      if (!canChangeShowCase && !lastApprovalLimbIsOwn && moderationInfoLimb) return true

      if (canChangeShowCase) {
        // if (selected === 1 && !canApprove) return true
        if (translateLastApproval === selected) return true
        // if (selected === 0 && !canDisapprove && approvals.length > 0) return true

        return selected === undefined
      }

      if (translateLastApproval === selectedLimb && !canApproveLimb) return true
      // if (selectedLimb === 0 && !canDisapproveLimb && approvals.length > 0) return true

      return selectedLimb === undefined

    }
    , [canChangeShowCase, lastApprovalLimbIsOwn, isApproved, isTagModer, lastApprovesExpert, moderationInfo, moderationInfoLimb, selectedLimb, canApproveLimb, selected, canApprove]
  )

  const canSaveAgeRestriction = useMemo(() => {
    //@ts-ignore
    return !!project?.isAgeRestricted !== projectAgeRestricted;
  }, [project, projectAgeRestricted])

  const canSaveBannedStatus = useMemo(() => {
    const initialProjectIsBanned = status === 3
    //@ts-ignore
    const statusChanged = initialProjectIsBanned !== projectBanned;
    
    if (projectBanned && blockingReasons.length === 0) {
      return false;
    }
    
    if (projectBanned && blockingReasons.includes(12) && !otherReasonText.trim()) {
      return false;
    }
    
    return statusChanged;
  }, [status, projectBanned, blockingReasons, otherReasonText])

  const canSaveBlockingReasons = useMemo(() => {
    if (!projectBanned) return false;
    
    if (blockingReasons.includes(12) && !otherReasonText.trim()) {
      return false;
    }
    
    if (blockingReasons.length === 0) return false;
    
    // Временно: запрещаем изменение причин если они уже сохранены (до реализации на бэке)
    const projectAny = project as any;
    const savedReasons = projectAny?.lastBannedInfo?.bannedReasonInfo?.reasons || [];
    
    if (savedReasons.length > 0) {
      return false;
    }
    
    const savedOtherReason = projectAny?.lastBannedInfo?.bannedReasonInfo?.otherReason || '';
    
    const reasonsChanged = JSON.stringify([...blockingReasons].sort()) !== JSON.stringify([...savedReasons].sort());
    const currentOtherReason = otherReasonText.trim();
    const savedOtherReasonNormalized = savedOtherReason?.trim() || '';
    const otherReasonChanged = currentOtherReason !== savedOtherReasonNormalized;
    
    return reasonsChanged || otherReasonChanged;
  }, [projectBanned, blockingReasons, otherReasonText, project])

  const showBlockingReasonsDropdown = useMemo(() => {
    return projectBanned && canChangeBannedStatus;
  }, [projectBanned, canChangeBannedStatus])

  const hasExistingReasons = useMemo(() => {
    const projectAny = project as any;
    return projectAny?.lastBannedInfo?.bannedReasonInfo?.reasons?.length > 0;
  }, [project])

  const hasUnbanRequest = useMemo(() => {
    //@ts-ignore
    return project?.contexts?.includes('unbanrequest');
  }, [project])

  const canSaveNomination = useMemo(() => {
    //@ts-ignore
    return (!!project?.contexts?.find((context: string) => context.startsWith('nomination')) !== projectNominated)
  }, [project, projectNominated])

  const buttonDisabled = disableSaveContexts && !canSaveAgeRestriction && !canSaveBannedStatus && !canSaveBlockingReasons

  useEffect(() => {
    setProjectAgeRestricted(isAgeRestricted);
    if (contexts?.length) {
      const nomination = contexts.find((context: string) => {
        return context.startsWith('nomination')
      })
      setProjectNominated(!!nomination)
      setLastNomination(nomination)
    }
    // setProjectNominated(!!contexts?.includes('nomination'))
  }, [isAgeRestricted, contexts])

  useEffect(() => {
    setProjectBanned(status === 3);
  }, [status])

  const disabledText = useMemo(() => {
    if (!buttonDisabled) {
      return null;
    }

    if (lastApprovesExpert && !canChangeShowCase && canChangeLimb) {
      return t('project_moderated_by_expert');
    }

    if (canChangeShowCase) {
      // if (onHseDesign && selected === 1) return 'Проект уже одобрен';
      if (translateLastApproval === YES && selected === YES) return t('project_already_approved');
      if (translateLastApproval === NO && (isTagModer ? selectedLimb : selected) === NO) return t('project_already_disapproved');
    }

    if (!canChangeShowCase && !lastApprovalLimbIsOwn && moderationInfoLimb) return t('not_your_approve');

    if (translateLastApproval === YES && selectedLimb === YES) return t('project_already_approved');
    if (translateLastApproval === NO && selectedLimb === NO) return t('project_already_disapproved');

    return t('choose_variant');
  }, [moderationInfoLimb, buttonDisabled, canChangeShowCase, translateLastApproval, canChangeLimb, isApproved, lastApprovalLimbIsOwn, onHseDesignTemp, selectedLimb, onHseDesign, selected, t])

  const isMobile = useIsMobile();
  const is1440Width = useBreakpoint(LayoutConst.screen.mac13);
  const [is920Height, setIs920Height] = useState(typeof window !== 'undefined' && window.innerHeight <= LayoutConst.screen.height.projectCover);
  const is1440 = is1440Width || is920Height;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleResize = () => {
      setIs920Height(window.innerHeight <= LayoutConst.screen.height.projectCover);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isPlayerOrMatchRoute) {
      return;
    }
    if (project?.projectId !== id) {
      reloadProject && reloadProject(Date.now())
    }
  }, [project, id, reloadProject, isPlayerOrMatchRoute])

  useEffect(() => {
    if (moderationInfo) {
      setSelected(isApproved ? 1 : 0)
    }
  }, [setSelected, onHseDesign, onHseDesignTemp, moderationInfo, isApproved])
  useEffect(() => {
    if (moderationInfoLimb) {
      setSelectedLimb(isApprovedLimb ? 1 : 0)
    }
  }, [moderationInfoLimb, isApprovedLimb])

  //@ts-ignore
  const projectColor = useMemo(() => (project && project?.additionalInfo?.color), [project])

  const handleSubmitContexts = useCallback(async () => {

    const context = canChangeShowCase ? 'hsedesign' : 'hsedesigntemp'
    // const contextMsg = 'hsedesign';
    const contextMsg = '';

    const updatedContexts = []

    if (canChangeShowCase && canSaveNomination) {
      updatedContexts.push({
        context: projectNominated ? 'nomination' : lastNomination,
        operation: projectNominated ? 1 : 0
      })
    }

    if ((canChangeShowCase ? selected : selectedLimb) !== undefined && !disableSaveContexts) {
      updatedContexts.push({
        context,
        operation: (canChangeShowCase ? selected : selectedLimb)
      })
    }

    if (canSaveBannedStatus || canSaveBlockingReasons) {
      const bannedData = projectBanned && blockingReasons.length > 0 ? {
        reasons: blockingReasons,
        otherReason: blockingReasons.includes(12) ? otherReasonText : ''
      } : null;

      const stringifiedData = bannedData ? JSON.stringify(bannedData) : undefined;

      updatedContexts.push({
        context: 'banned',
        operation: projectBanned ? 1 : 0,
        data: stringifiedData
      })
    }

    const requestBody = {
      projectId: project.projectId,
      contexts: updatedContexts,
      isAgeRestricted: projectAgeRestricted
    };

    await API.request()
        .endpoint(Endpoints.geograffee)
        .url(`/api/Project/UpdateContexts`)
        .post()
        .body(requestBody)
        .send()
        .then((res) => {
          if (res.status === 200) {
            const infoMsgApproved = canChangeShowCase
              ? (selected ? `${t('project_approved_notification')} ${contextMsg}` : `${t('project_disapproved_notification')} ${contextMsg}`)
              : (selectedLimb ? `${t('project_approved_limb_notification')}  ${contextMsg}` : `${t('project_disapproved_limb_notification')} ${contextMsg}`)
            ;

            const infoMsgAge = projectAgeRestricted ? t('project_age_restricted_notification') : t('project_not_age_restricted_notification')

            const infoMsgNominated = projectNominated ? t('project_nominated') : t('project_not_nominated')

            const infoMessageBanned = projectBanned ? t('project_edit.info.project_banned') : t('project_edit.info.project_unbanned')

            if (!canChangeShowCase && selectedLimb !== undefined && !disableSaveContexts) {
              toasts.info(infoMsgApproved);
            }

            if (canChangeShowCase && selected !== undefined && !disableSaveContexts) {
              toasts.info(infoMsgApproved);
            }
            //@ts-ignore
            if (projectAgeRestricted !== !!project?.isAgeRestricted) {
              toasts.info(infoMsgAge)
            }

            if (!!project?.contexts?.includes('nomination') !== projectNominated) {
              toasts.info(infoMsgNominated)
            }

            if (projectBanned !== (project?.status === 3)) {
              toasts.info(infoMessageBanned)
            }
            
            window.location.reload();
          }
          if (res.status === 400) {
            toasts.info(!lastApprovalLimbIsOwn ? t('project_approved_by_other') : t('can_not_do'));
          }
        }).catch((e) => {
          console.error('Ошибка при отправке данных:', e);
          console.error('Error details:', e.response || e);
          toasts.info('Ошибка запроса');
        });
  }, [selected,
    canChangeShowCase,
    canSaveNomination,
    disableSaveContexts,
    project.projectId,
    //@ts-ignore
    project?.isAgeRestricted,
    project?.contexts,
    projectAgeRestricted,
    projectNominated,
    projectBanned,
    lastNomination,
    toasts,
    lastApprovalLimbIsOwn,
  ])

  const toggleAgeRestriction = useCallback(() => {
    setProjectAgeRestricted(!projectAgeRestricted)
  }, [projectAgeRestricted])

  const toggleProjectBan = useCallback(() => {
    const newBlockedState = !projectBanned;
    setProjectBanned(newBlockedState)
    // Не очищаем причины при снятии чекбокса — они должны сохраняться
    
    if (onBlockedStateChange) {
      onBlockedStateChange(newBlockedState, blockingReasons, otherReasonText);
    }
  }, [projectBanned, blockingReasons, otherReasonText, onBlockedStateChange])

  const handleBlockingReasonsChange = useCallback((selectedOptions: readonly any[] | null) => {
    const values = selectedOptions ? selectedOptions.map((option: any) => option.value) : [];
    
    // Ограничение: не более 3 причин
    if (values.length > 3) {
      toasts.info(t('project_edit.max_blocking_reasons_error'));
      return;
    }
    
    setBlockingReasons(values);
    
    if (!values.includes('other') || (values.includes('other') && !otherReasonText.trim())) {
      setOtherReasonText('');
    }
    
    if (onBlockedStateChange && projectBanned) {
      const finalOtherReason = values.includes('other') && otherReasonText.trim() ? otherReasonText : '';
      onBlockedStateChange(projectBanned, values, finalOtherReason);
    }
  }, [otherReasonText, onBlockedStateChange, projectBanned, toasts, t]);

  const handleOtherReasonTextChange = useCallback((ev: any) => {
    const value = ev.target.value;
    // Ограничиваем до 75 символов
    if (value.length <= 75) {
      setOtherReasonText(value);
      
      // Уведомляем родительский компонент об изменении состояния
      if (onBlockedStateChange && projectBanned) {
        onBlockedStateChange(projectBanned, blockingReasons, value);
      }
    }
  }, [onBlockedStateChange, projectBanned, blockingReasons]);

  const toggleNomination = useCallback(() => {
    setProjectNominated(!projectNominated)
    setSelected(YES);
  }, [projectNominated])

  const approvalTitleText = useMemo(() => {
    if (isTagModer && !canChangeShowCase) {
      return t('teacher_text')
    }

    return t('teacher_approved_text')
  } , [isTagModer, t])

  const radioCheckedStatusTrue = useMemo(() => {
    if (disabledText === t('project_moderated_by_expert')) {
      return selected;
    } 
    if (disabledText === t('not_your_approve')) {
      return selectedLimb
    }
    return (canChangeShowCase ? selected : selectedLimb) === YES
  } , [selected, selectedLimb, disabledText])

  const radioCheckedStatusFalse = useMemo(() => {
    if (disabledText === t('project_moderated_by_expert')) {
      return !selected;
    } 
    if (disabledText === t('not_your_approve')) {
      return !selectedLimb
    }
    return (canChangeShowCase ? selected : selectedLimb) === NO
  } , [selected, selectedLimb, disabledText])

  const onRadioStatusChange = useCallback((type: number) => {
    if (
      disabledText === t('project_moderated_by_expert') || 
      disabledText === t('not_your_approve')) {
      return;
    }

    if (type === YES) {
      canChangeShowCase ? setSelected(YES) : setSelectedLimb(YES);
      try {
        window.dispatchEvent(new CustomEvent('approval-radio-change', { 
          detail: { target: canChangeShowCase ? 'main' : 'limb', value: YES } 
        }));
      } catch {}
      return;
    }

    canChangeShowCase ? setSelected(NO) : setSelectedLimb(NO);
    try {
      window.dispatchEvent(new CustomEvent('approval-radio-change', { 
        detail: { target: canChangeShowCase ? 'main' : 'limb', value: NO } 
      }));
    } catch {}
  } , [canChangeShowCase, disabledText, t])

  if (!showModal) {
    return null;
  }

  const Radio = (props: any) => {
   
    
    return (
      <CheckBoxWrapper disabled={props.disabled}>
        <FlexBox>
          <RadioInputSource {...props as any} type={'radio'}/>
          <RadioBox {...props as any}>
            <RadioCircle {...props as any} />
            <RadioLabel {...props as any} >
              {props.title}
            </RadioLabel>
          </RadioBox>
        </FlexBox>
      </CheckBoxWrapper>
    )
  }

  return (
    <FlexBox direction="column" directionMobile="column" width="100%" gy={16} gyMobile={8}>
      <ApprovalDialogWrapper
        className='approval-dialog-wrap'
        width={'100%'}
        direction="column"
        align={"center"}
        justify={"center"}
        offsetLeft={!isMobile && !fullWidth ? 24 : 0}
        isProjectPage={isProjectPage}
        wide={wide}
        fullWidth={fullWidth}
        showBlockingReasons={showBlockingReasonsDropdown}
      >
        <FlexBox className={'left-column-container'} width="100%">
        {canChangeShowCase && <ContentWrapper offsetBottomMobile={4} align={"center"} justify={"space-between"} direction={"row"} gy={0}
                                              isProjectPage={isProjectPage}>
          <FlexBox direction="column" directionMobile="column" width="100%">
            <FlexBox align="center" alignMobile="center" justifyMobile="flex-start" directionMobile='row'
                     justify='flex-start' gyMobile={8}>
              <ApprovalTitle
                isProjectPage={isProjectPage}
                color={theme.projectView.titleColor}>
                {approvalTitleText}
              </ApprovalTitle>

              <RadioButtonWrapper isProjectPage={isProjectPage}>
                <Radio
                  onChange={() => false}
                  name={`approval-readonly-${isBottomMenu ? 'bottom' : 'top'}-${isProjectPage ? 'project' : 'main'}`}
                  title={t('yes')}
                  checked={lastLimbStatus}
                  clr={getApprovalFontColor(projectColor)}
                  disabled
                  approval
                  firstRadioButton
                />
                <Radio
                  onChange={() => false}
                  name={`approval-readonly-${isBottomMenu ? 'bottom' : 'top'}-${isProjectPage ? 'project' : 'main'}`}
                  title={t('no')}
                  checked={lastHSEApproveLimbIndex !== -1 && !lastLimbStatus }
                  clr={getApprovalFontColor(projectColor)}
                  disabled
                  approval
                />
              </RadioButtonWrapper>
            </FlexBox>
          </FlexBox>
        </ContentWrapper>}
        {(canChangeShowCase || canChangeLimb) && <ContentWrapper offsetBottomMobile={4} align={"center"} justify={"space-between"} direction={"row"} gy={0}
                        isProjectPage={isProjectPage}>
          <FlexBox direction="column" directionMobile="column" width="100%">
            <FlexBox align="center" alignMobile='center' justify='flex-start' justifyMobile="flex-start"
                     directionMobile='row' gyMobile={8}>
              <ApprovalTitle
                isProjectPage={isProjectPage}
                color={theme.projectView.titleColor}>
                {t('teacher_text')}
              </ApprovalTitle>
              <RadioButtonWrapper isProjectPage={isProjectPage} data-tip={disabledText}>
                <Radio
                  onChange={() => onRadioStatusChange(YES)}
                  name={`approval-${isBottomMenu ? 'bottom' : 'top'}-${isProjectPage ? 'project' : 'main'}`}
                  title={t('yes')}
                  checked={radioCheckedStatusTrue}
                  clr={getApprovalFontColor(projectColor)}
                  approval
                  firstRadioButton
                />
                <Radio
                  onChange={() => onRadioStatusChange(NO)}
                  name={`approval-${isBottomMenu ? 'bottom' : 'top'}-${isProjectPage ? 'project' : 'main'}`}
                  title={t('no')}
                  checked={radioCheckedStatusFalse}
                  clr={getApprovalFontColor(projectColor)}
                  approval
                />
              </RadioButtonWrapper>
            </FlexBox>
          </FlexBox>
        </ContentWrapper>}
        <ContentWrapper align={"center"} justify={"space-between"} direction={"row"} gy={0}
                        isProjectPage={isProjectPage}>
          <FlexBox direction="column" directionMobile="column" width="100%">
            <CheckboxHolder isBottomMenu={isBottomMenu}>
              <MagicBoxRow 
                onClick={toggleAgeRestriction}
                checked={projectAgeRestricted}
                label={t('project_edit.age_restricted')}
              />
              {/* <CheckboxField
                onClick={toggleAgeRestriction}
                checked={projectAgeRestricted}
                label={t('project_edit.age_restricted')}
                dark

              /> */}
            </CheckboxHolder>
          </FlexBox>
        </ContentWrapper>
        {canChangeShowCase && <ContentWrapper align={"center"} justify={"space-between"} direction={"row"} gy={0}
                                              isProjectPage={isProjectPage}>
          <FlexBox direction="column" directionMobile="column" width="100%">
            <CheckboxHolder isBottomMenu={isBottomMenu}>
              <MagicBoxRow 
                onClick={toggleNomination}
                checked={projectNominated}
                label={t('project_edit.nominate_project')}
              />
              {/* <CheckboxField
                onClick={toggleNomination}
                checked={projectNominated}
                label={t('project_edit.nominate_project')}
                dark

              /> */}
            </CheckboxHolder>
          </FlexBox>
        </ContentWrapper>}
        {canChangeBannedStatus && (
          <ContentWrapper align={"center"} justify={"space-between"} direction={"row"} gy={0}
                          isProjectPage={isProjectPage}>
            <FlexBox direction="column" directionMobile="column" width="100%">
              <CheckboxHolder isBottomMenu={isBottomMenu}>
                <MagicBoxRow 
                  onClick={toggleProjectBan}
                  checked={projectBanned}
                  label={t('project_edit.project_banned')}
                />
              {/* <CheckboxField
                  onClick={toggleProjectBan}
                  checked={projectBanned}
                  label={t('project_edit.project_banned')}
                  dark
                /> */}
              </CheckboxHolder>
              {showBlockingReasonsDropdown && (
                <BlockingReasonsWrapper showBlockingReasons={showBlockingReasonsDropdown}>
                  {hasUnbanRequest ? (
                    <>
                      <BlockingReasonsLabel>
                        {t('project_edit.select_blocking_reason')}
                      </BlockingReasonsLabel>
                      <BlockingReasonsSelect
                        options={[]}
                        value={[{ value: -1, label: t('project_edit.project_under_review') }]}
                        placeholder={t('project_edit.project_under_review')}
                        isOptionDisabled={() => true}
                        onChange={() => {}}
                        isDisabled={true}
                        theme={theme}
                        isMobile={isMobile}
                        is1440={is1440}
                      />
                    </>
                  ) : (
                    <>
                      <BlockingReasonsLabel>
                        {t('project_edit.select_blocking_reason')} ({blockingReasons.length}/3)
                      </BlockingReasonsLabel>
                      <BlockingReasonsSelect
                        options={BLOCKING_REASONS.map(r => ({ value: r.value, label: t(r.labelKey) }))}
                        value={BLOCKING_REASONS
                          .filter(r => blockingReasons.includes(r.value))
                          .map(r => ({ value: r.value, label: t(r.labelKey) }))
                        }
                        placeholder={t('project_edit.select_blocking_reason_placeholder')}
                        isOptionDisabled={(option: any) => !blockingReasons.includes(option.value) && blockingReasons.length >= 3}
                        onChange={handleBlockingReasonsChange}
                        isDisabled={hasExistingReasons}
                        theme={theme}
                        isMobile={isMobile}
                        is1440={is1440}
                      />
                      {blockingReasons.includes(12) && (
                        <OtherReasonInputWrapper>
                          <BlockingReasonsLabel>
                            {t('project_edit.other_reason_description')} ({otherReasonText.length}/75)
                          </BlockingReasonsLabel>
                          <OtherReasonInput
                            value={otherReasonText}
                            onChange={handleOtherReasonTextChange}
                            placeholder={t('project_edit.select_blocking_reason_placeholder')}
                            maxLength={75}
                            disabled={hasExistingReasons}
                          />
                        </OtherReasonInputWrapper>
                      )}
                    </>
                  )}
                </BlockingReasonsWrapper>
              )}
            </FlexBox>
          </ContentWrapper>
        )}
        </FlexBox>
        <ButtonHolder>
          <div data-tip={disabledText}>
            <ApproveProjectButton
              isProjectPage={isProjectPage}
              onClick={handleSubmitContexts}
              disabled={disableSaveContexts && !canSaveAgeRestriction && !canSaveNomination && !canSaveBannedStatus && !canSaveBlockingReasons}
            >
              {t('save')}
            </ApproveProjectButton>
          </div>
        </ButtonHolder>
        <ReactTooltip/>
      </ApprovalDialogWrapper>
    </FlexBox>
  );
};

export default memo(ApprovalDialog)
